/**
 * 계약 청구 스케줄 컴포넌트
 * 계약서의 청구 스케줄을 표시하고 관리하는 컴포넌트입니다.
 * 대여료와 유지보수료의 청구 정보를 표시하고 편집할 수 있습니다.
 */

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BillingDetail } from "src/api/billingSchedule/billing-type";
import { getContractPendingBillList } from "src/api/contract/contract-api";
import { ContractStep, DetailTotalData } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { BaseButton } from "src/components/BaseButton";
import { BaseModal } from "src/components/BaseModal";
import BaseSectionTitle from "src/components/layout/BaseSectionTitle";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import pagePath from "src/pagePath.json";
import { useModal } from "src/recoil/modalState/hook";
import { useS2Contract } from "../hooks/useS2Contract";
import ContractBillingScheduleConfirmModal from "./ContractBillingScheduleConfirmModal";
import BillingScheduleDetail from "./detailComponent/BillingScheduleDetail";
import BillingScheduleEdit from "./editComponent/BillingScheduleEdit";

/**
 * 계약 청구 스케줄 컴포넌트의 props 인터페이스
 */
interface BasicProps {
  detailData: DetailTotalData; // 계약 상세 정보
  setData: Function; // 데이터 업데이트 함수
}

// ArticleSection에서 사용하는 상수들
const NoEditStateNoS2: Array<string> = [
  ContractStep.APPLY_CANCEL, // 신청취소
  ContractStep.TERMINATE_COMPLETE, // 해지완료
  ContractStep.USE_COMPLETE, // 이용완료
];

const NoEditStateS2: Array<string> = NoEditStateNoS2.concat([
  ContractStep.CONTRACT_ACCEPT,
  ContractStep.USE_APPROVAL,
  ContractStep.USE_PROGRESS,
  ContractStep.USE_COMPLETE,
]);

// 신청/계약 상태별 → ‘이용료 결제 정보’ 저장 버튼 클릭 시 청구목록 모달
const BILLING_CONTRACT_STATUS = [
  ContractStep.CONTRACT_ACCEPT, // 계약체결
  ContractStep.USE_APPROVAL, // 이용승인
  ContractStep.USE_PROGRESS, // 이용중
  ContractStep.TERMINATE_RECEIVED, // 해지접수
];

/**
 * 계약 청구 스케줄 컴포넌트
 * @param detailData 계약 상세 정보
 * @param setData 데이터 업데이트 함수
 */
const ContractBillingSchedule = ({ detailData, setData }: BasicProps) => {
  const { setAbstractModalZ1 } = useModal();
  const { partnerId } = useParams();
  const { isS2Contract } = useS2Contract();
  const { isAuthority } = usePartnerAuthority();
  const R = "RENTAL"; // 대여 유형 상수
  const [editable, setEditable] = useState(false); // 편집 모드 상태
  const [saveFlags, setSaveFlags] = useState(false); // 저장 상태 플래그

  const { contract, contractManage, scheduleList, provider } = detailData;

  const [textGrey] = useState("contents-container__grid "); // 텍스트 스타일 클래스
  const [rentalSchedule, setRentalSchedule] = useState<BillingDetail>(); // 대여료 스케줄
  const [mntSchedule, setMntSchedule] = useState<BillingDetail[]>(); // 유지보수료 스케줄
  const [popupState, setPopupState] = useState(false); // 팝업 상태
  const [editConfrim, setEditConfrim] = useState(false); // 편집 확인 상태
  const [commonModals, setCommonModals] = useState<any>({
    // 공통 모달 상태
    message: "",
    title: "",
  });

  const { executeAsync: getPendingBillList } = useApiOperation(getContractPendingBillList);

  // 계약 > 이용료 결제정보 > 스케줄 적용대상 청구서 조회
  const getPendingBillListData = async () => {
    const rentalScheduleId = scheduleList?.find((sd: BillingDetail) => sd.supplyType === "RENTAL")?.scheduleId;
    const { data } = await getPendingBillList({ scheduleId: String(rentalScheduleId) });
    return data.data.pendingBillList;
  };

  /**
   * 청구 스케줄 상세 목록 페이지로 이동하는 함수
   */
  const gotoBillingScheduleDetailList = () => {
    const billingPath =
      pagePath.billing.detail.replace(":partnerId", String(partnerId)).replace(":id", String(contract?.contractId)) + "?tab=schedule";
    const billingS2Path =
      pagePath.billingS2.detail.replace(":partnerId", String(partnerId)).replace(":id", String(contract?.contractId)) + "?tab=schedule";
    let path = isS2Contract ? billingS2Path : billingPath;
    window.open(path, "_blank", "noreferrer");
  };

  /**
   * 상세 데이터가 변경될 때 대여료와 유지보수료 스케줄을 설정하는 효과
   */
  useEffect(() => {
    if (detailData) {
      const schedule = scheduleList?.find((sd: BillingDetail) => sd.supplyType === R);
      const mntList = scheduleList?.filter((sd: BillingDetail) => sd.supplyType !== R);

      setRentalSchedule(schedule);
      setMntSchedule(mntList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailData]);

  // ArticleSection에서 분리된 로직
  // const title = detailData?.product.productType === "TIME_COURT" ? "결제 정보" : "이용료 결제 정보";
  const disabledState = [String(detailData?.contract?.contractStep), String(detailData?.product.productType)];

  const isEditDisabled = disabledState[1] !== "TIME_COURT" ? NoEditStateNoS2.includes(disabledState[0]) : !NoEditStateS2.includes(disabledState[0]);

  const handleSave = () => {
    setSaveFlags(true);
  };

  console.log("deta", detailData);
  return (
    <article className="contents-container__wrap-article full-width">
      {isAuthority("w") && (
        <div className="contents-container__edit-area">
          {editable ? (
            <div className="flex-center">
              <BaseButton type="button" className="size-small color-white mr10" title="취소" onClick={() => setEditable(false)} />
              <BaseButton
                type="button"
                className="size-small"
                title="저장"
                onClick={() => {
                  // 공간임대 상품 && BILLING_CONTRACT_STATUS 에 포함된 계약 상태인 경우 청구서 저장 확인 모달 표시
                  if (
                    detailData?.product.productType === "FULL_COURT" &&
                    BILLING_CONTRACT_STATUS.includes(detailData.contract?.contractStep as ContractStep)
                  ) {
                    const handleSaveConfirm = async () => {
                      const pendingBillList = await getPendingBillListData();
                      setAbstractModalZ1({
                        isOpen: true,
                        size: "large",
                        children: (
                          <ContractBillingScheduleConfirmModal
                            pendingBillList={pendingBillList}
                            onClose={() => setAbstractModalZ1({ isOpen: false })}
                            onSave={() => {
                              handleSave();
                              setAbstractModalZ1({ isOpen: false });
                            }}
                          />
                        ),
                      });
                    };

                    handleSaveConfirm();
                  } else {
                    // 기존 로직 실행
                    setEditConfrim(true);
                  }
                }}
              />
            </div>
          ) : (
            <button className="base-edit-btn" disabled={isEditDisabled} onClick={() => setEditable(true)} />
          )}
        </div>
      )}

      <BaseSectionTitle title={detailData?.product.productType === "TIME_COURT" ? "결제 정보" : "이용료 결제 정보"} noHashLink={false} />

      <section className="contents-container__1160">
        {editable === false ? (
          <BillingScheduleDetail
            detailData={detailData}
            rentalSchedule={rentalSchedule!}
            contract={contract!}
            gotoBillingScheduleDetailList={gotoBillingScheduleDetailList}
            textGrey={textGrey}
          />
        ) : (
          <BillingScheduleEdit
            detailData={detailData}
            saveFlags={saveFlags}
            setSaveFlags={setSaveFlags}
            setEditable={setEditable}
            contract={contract!}
            textGrey={textGrey}
            contractManage={contractManage!}
            rentalSchedule={rentalSchedule!}
            gotoBillingScheduleDetailList={gotoBillingScheduleDetailList}
            provider={provider!}
            setData={setData}
            popupState={popupState}
            setPopupState={setPopupState}
            setCommonModals={setCommonModals}
            setEditConfrim={setEditConfrim}
          />
        )}
      </section>

      {/* 팝업 모달 */}
      {popupState && (
        <BaseModal isOpen={popupState} btnRightTitle="확인" onClick={() => setPopupState(false)} title={commonModals.title}>
          {commonModals.subTitle && <p>{commonModals.subTitle}</p>}
          <p>{commonModals.message}</p>
        </BaseModal>
      )}

      {/* 저장 확인 모달 */}
      {editConfrim && (
        <BaseModal
          isOpen={editConfrim}
          btnLeftTitle="취소"
          btnRightTitle="확인"
          onClick={() => {
            console.log("click!!");
            console.log("?????", handleSave);
            handleSave();
          }}
          onClose={() => setEditConfrim(false)}
          title={detailData?.product.productType === "TIME_COURT" ? "결제 정보" : "이용료 결제 정보"}
        >
          <p>저장하시겠습니까?</p>
        </BaseModal>
      )}
    </article>
  );
};
export default ContractBillingSchedule;
